import { useEffect, useState } from "react";
import "./App.css";
import AppRoutes from "./components/AppRoutes";

import {
  buildAppInstallationInfo,
  configureLDUStates,
  FingerprintJSProvider,
  getAuthorizationHeader,
  IAppInstallationInfo,
  initializeAmplifyAuth,
} from "@brandclub/common-ui";
import { GetAppConfigFromServerResponse } from "./api/rest/unauthenticated/AppConfig";
import StoreFullScreenLoading from "./components/StoreComponents/StoreFullScreenLoading";
import { useAppDispatch, useAppSelector } from "./redux/hooks";
import {
  getAppConfig,
  getBrandAppConfigs,
} from "./redux/reducers/appConfig/thunk";
import axiosInstance from "./utils/axios";
import ConfigUtils from "./utils/ConfigUtils";
import FaviconManager from "./components/FaviconManager";

export const updateAppInstallationInfoInLandingPage =
  async (appInstallationInfo: {
    deviceInfo: any;
    appInfo: any;
  }): Promise<any> => {
    const headers: Record<any, any> = {
      ...(await getAuthorizationHeader()),
    };
    const res = await axiosInstance.post(
      "/appInstallation/update",
      appInstallationInfo,
      {
        headers,
      }
    );

    const { status, payload } = res.data || {};
    return status === "success" ? payload : undefined;
  };

const App = () => {
  const authConfig = useAppSelector(({ appConfig }) => appConfig?.authConfig);
  const domainConfig = useAppSelector(
    ({ appConfig }) => appConfig?.domainConfig
  );
  const userId = useAppSelector(({ userProfile }) => userProfile?.userId);
  const [isAuthInitialized, setIsAuthInitialized] = useState<boolean>(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    (async () => {
      if (authConfig && domainConfig) {
        await initializeAmplifyAuth(authConfig, domainConfig?.domainName);
        setIsAuthInitialized(true);

        try {
          if (userId) {
            const appId = ConfigUtils.getWebAppId();
            const appInstallationInfo: IAppInstallationInfo =
              await buildAppInstallationInfo({ userId, appId });
            if (appInstallationInfo) {
              await updateAppInstallationInfoInLandingPage(appInstallationInfo);
            }
          }
        } catch (e) {
          console.error(
            "An error occurred when building appInstallationInfo: ",
            e
          );
        }
      } else {
        const payload = (await dispatch(getAppConfig()))
          .payload as GetAppConfigFromServerResponse;
        if (payload.appConfig?.domainConfig) {
          // if there's no appConfig, get the brand app config as soon as we have the appConfig response to avoid race conditions
          dispatch(
            getBrandAppConfigs({
              appId: payload.appConfig.domainConfig.domainName,
            })
          );
        }
      }
    })();
  }, [authConfig, dispatch, userId, domainConfig]);

  // Configure LDU affected states from app config (falls back to defaults if not provided)
  useEffect(() => {
    if (domainConfig) {
      configureLDUStates(domainConfig.lduAffectedStates);
    }
  }, [domainConfig]);

  // we should let the app load until we have the app config and cognito initialized
  if (!authConfig || !isAuthInitialized) {
    return <StoreFullScreenLoading />;
  }

  return (
    <FingerprintJSProvider
      hostDomainName={`https://${window.location.hostname}`}
    >
      <FaviconManager />
      <AppRoutes />
    </FingerprintJSProvider>
  );
};

export default App;
